import { thunk } from 'easy-peasy';

import {
  DocumentType,
  FilterParams,
  IBurnTokenPayload,
  ICommunityAddAuditDocumentsForm,
  ICommunityForm,
  IMarkCommunityForDeletion,
  IProjectEvent,
  IProjectMilestone,
  IProjectStatement,
  IStoreModel,
  ISubmitTokensRequestPayload,
} from 'types';
import communityAPI from 'api/community/communityAPI';
import { buildCreateDocumentsRequestBody } from 'utils';
import { PlatformContract } from 'blockchain';
import { validateWalletForTransaction } from 'utils/validateTransaction';
import { CommunityActionsAndThunks, ICommunityThunks } from 'types/store/community';
import communityRequestAPI from 'api/community/communityRequestAPI';

export const thunks: ICommunityThunks = {
  loadCommunitiesList: thunk<CommunityActionsAndThunks, FilterParams>(async (actions, payload) => {
    actions.setCommunityLoading(true);

    try {
      const communities = await communityAPI.getCommunities(payload);
      actions.setCommunitiesList(communities);
    } finally {
      actions.setCommunityLoading(false);
    }
  }),
  loadTopCommunitiesList: thunk<CommunityActionsAndThunks, FilterParams>(async (actions, payload) => {
    actions.setCommunityLoading(true);

    try {
      const communities = await communityAPI.getCommunities(payload);
      actions.setTopCommunitiesList(communities);
    } finally {
      actions.setCommunityLoading(false);
    }
  }),
  loadTopEmissionReductionsCommunitiesList: thunk<CommunityActionsAndThunks, FilterParams>(async (actions, payload) => {
    actions.setCommunityLoading(true);

    try {
      const communities = await communityAPI.getCommunities(payload);
      actions.setTopEmissionReductionsCommunitiesList(communities);
    } finally {
      actions.setCommunityLoading(false);
    }
  }),
  loadNewCommunitiesList: thunk<CommunityActionsAndThunks, FilterParams>(async (actions, payload) => {
    actions.setCommunityLoading(true);

    try {
      const communities = await communityAPI.getCommunities(payload);
      actions.setNewCommunitiesList(communities);
    } finally {
      actions.setCommunityLoading(false);
    }
  }),
  loadCommunityById: thunk<CommunityActionsAndThunks, number, unknown, IStoreModel>(async (actions, payload) => {
    actions.setCommunityLoading(true);
    try {
      const communityById = await communityAPI.getCommunityById(payload);
      actions.setCommunityById(communityById);
    } finally {
      actions.setCommunityLoading(false);
    }
  }),
  loadCommunity: thunk<CommunityActionsAndThunks>(async (actions) => {
    actions.setCommunityLoading(true);

    try {
      const community = await communityAPI.getMyCommunity();
      actions.setCommunity(community);
    } finally {
      actions.setCommunityLoading(false);
    }
  }),
  updateCommunity: thunk<CommunityActionsAndThunks, ICommunityForm, unknown, IStoreModel>(
    async (actions, payload, { getStoreActions }) => {
      const storeActions = getStoreActions();
      const {
        cover,
        pipelineDocuments,
        registrationDocuments,
        issuanceDocuments,
        otherDocuments,
        companyLegalDocuments,
        founderLegalDocuments,
        auditLegalDocuments,
        emissionReductions,
        price,
        ...body
      } = payload;
      if (!body.standard) {
        delete body.standard;
      }

      const uploadUrls = await communityAPI.updateMyCommunity({
        ...body,
        emissionReductions: emissionReductions.toString(),
        price: Number(price),
        documents: [
          ...buildCreateDocumentsRequestBody(cover, DocumentType.CoverImage),
          ...buildCreateDocumentsRequestBody(pipelineDocuments, DocumentType.VcsPipeline),
          ...buildCreateDocumentsRequestBody(registrationDocuments, DocumentType.VcsRegistration),
          ...buildCreateDocumentsRequestBody(issuanceDocuments, DocumentType.VcsIssuance),
          ...buildCreateDocumentsRequestBody(otherDocuments, DocumentType.VcsOther),
          ...buildCreateDocumentsRequestBody(companyLegalDocuments, DocumentType.CompanyLegal),
          ...buildCreateDocumentsRequestBody(founderLegalDocuments, DocumentType.FounderLegal),
          ...buildCreateDocumentsRequestBody(auditLegalDocuments, DocumentType.AuditCompanyLegal),
        ],
      });

      if (uploadUrls) {
        await storeActions.document.uploadDocumentInputs({
          uploadUrls,
          documents: [
            ...cover,
            ...pipelineDocuments,
            ...registrationDocuments,
            ...issuanceDocuments,
            ...otherDocuments,
            ...companyLegalDocuments,
            ...founderLegalDocuments,
            ...auditLegalDocuments,
          ],
        });
      }
    },
  ),
  submitAuditDocuments: thunk<CommunityActionsAndThunks, ICommunityAddAuditDocumentsForm, unknown, IStoreModel>(
    async (actions, payload, { getStoreActions }) => {
      const storeActions = getStoreActions();
      const { documents } = payload;

      const uploadUrls = await communityAPI.submitAuditDocuments({
        documents: [...buildCreateDocumentsRequestBody(documents, DocumentType.AuditCompanyLegal)],
      });

      if (uploadUrls) {
        await storeActions.document.uploadDocumentInputs({
          uploadUrls,
          documents: [...documents],
        });
      }
    },
  ),
  submitCommunity: thunk<CommunityActionsAndThunks>(async () => {
    await communityAPI.submitMyCommunity();
  }),

  markForDeletion: thunk<CommunityActionsAndThunks, IMarkCommunityForDeletion, unknown, IStoreModel>(
    async (actions, payload) => {
      await communityAPI.markForDeletion(payload.id, {
        markedForDeletion: payload.markedForDeletion,
      });
    },
  ),

  submitTokensRequest: thunk<CommunityActionsAndThunks, ISubmitTokensRequestPayload, unknown, IStoreModel>(
    async (actions, payload, { getStoreActions }) => {
      if (payload.documents) {
        const storeActions = getStoreActions();
        const uploadUrls = await communityRequestAPI.issueTokens({
          amount: payload.amount,
          type: payload.type,
          documents: buildCreateDocumentsRequestBody(payload.documents),
        });

        if (uploadUrls) {
          await storeActions.document.uploadDocumentInputs({
            uploadUrls,
            documents: payload.documents,
          });
        }
      } else {
        await communityRequestAPI.issueTokens({
          amount: payload.amount,
          type: payload.type,
        });
      }
    },
  ),

  cancelTokenIssue: thunk<CommunityActionsAndThunks, { id: number }>(async (actions, payload) => {
    actions.setCommunityLoading(true);
    const { id } = payload;
    try {
      await communityRequestAPI.cancelTokenIssue(id);
      actions.loadCommunity();
    } finally {
      actions.setCommunityLoading(false);
    }
  }),

  //STATEMENTS

  loadCommunityStatements: thunk<CommunityActionsAndThunks, { projectId: number }>(async (actions, payload) => {
    const { projectId } = payload;

    const communityStatements = await communityAPI.getCommunityStatements(projectId);
    actions.setCommunityStatements(communityStatements);
  }),

  addCommunityStatement: thunk<CommunityActionsAndThunks, { projectId: number; statement: IProjectStatement }>(
    async (actions, payload) => {
      actions.setCommunityLoading(true);

      const { projectId, statement } = payload;
      try {
        await communityAPI.createCommunityStatement(projectId, statement);
      } finally {
        actions.setCommunityLoading(false);
      }
    },
  ),

  editCommunityStatement: thunk<
    CommunityActionsAndThunks,
    { projectId: number; statement: Omit<IProjectStatement, 'id'>; statementId: number }
  >(async (actions, payload) => {
    actions.setCommunityLoading(true);

    const { projectId, statement, statementId } = payload;
    try {
      await communityAPI.editCommunityStatement(projectId, statement, statementId);
    } finally {
      actions.setCommunityLoading(false);
    }
  }),

  deleteCommunityStatement: thunk<CommunityActionsAndThunks, { projectId: number; statementId: number }>(
    async (actions, payload) => {
      actions.setCommunityLoading(true);

      const { projectId, statementId } = payload;
      try {
        await communityAPI.deleteCommunityStatement(projectId, statementId);
      } finally {
        actions.setCommunityLoading(false);
      }
    },
  ),

  //PUBLICATIONS

  loadCommunityPublications: thunk<CommunityActionsAndThunks, { projectId: number; limit: number }>(
    async (actions, payload) => {
      const communities = await communityAPI.getCommunityPublications(payload);
      actions.setCommunityPublications(communities);
    },
  ),

  addCommunityPublication: thunk<
    CommunityActionsAndThunks,
    { projectId: number; publication: any },
    unknown,
    IStoreModel
  >(async (actions, payload, { getStoreActions }) => {
    actions.setCommunityLoading(true);
    const { projectId, publication } = payload;
    const storeActions = getStoreActions();
    try {
      const response = await communityAPI.createCommunityPublication(projectId, {
        ...publication,
        coverImage: buildCreateDocumentsRequestBody(publication.coverImage, DocumentType.CoverImage)[0],
      });
      await storeActions.document.uploadDocumentInputs({
        uploadUrls: [response.coverImage],
        documents: publication.coverImage,
      });
    } finally {
      actions.setCommunityLoading(false);
    }
  }),

  editCommunityPublication: thunk<
    CommunityActionsAndThunks,
    { projectId: number; publication: any; publicationId: number },
    unknown,
    IStoreModel
  >(async (actions, payload, { getStoreActions }) => {
    actions.setCommunityLoading(true);
    const { projectId, publication, publicationId } = payload;
    const storeActions = getStoreActions();
    try {
      if (Array.isArray(publication.coverImage) && publication.coverImage[0]?.url) {
        delete publication.coverImage;
      }
      const publicationBody = {
        ...publication,
      };

      if (publication.coverImage) {
        publicationBody.coverImage = buildCreateDocumentsRequestBody(
          publication.coverImage,
          DocumentType.CoverImage,
        )[0];
      }
      const editedCommunity = await communityAPI.editCommunityPublication(projectId, publicationBody, publicationId);
      if (publication.coverImage) {
        await storeActions.document.uploadDocumentInputs({
          uploadUrls: [editedCommunity.coverImage],
          documents: publication.coverImage,
        });
      }
    } finally {
      actions.setCommunityLoading(false);
    }
  }),

  deleteCommunityPublication: thunk<CommunityActionsAndThunks, { projectId: number; publicationId: number }>(
    async (actions, payload) => {
      actions.setCommunityLoading(true);

      const { projectId, publicationId } = payload;
      try {
        await communityAPI.deleteCommunityPublication(projectId, publicationId);
      } finally {
        actions.setCommunityLoading(false);
      }
    },
  ),

  //MILESTONES
  loadCommunityMilestones: thunk<CommunityActionsAndThunks, { projectId: number; limit: number }>(
    async (actions, payload) => {
      const projectMilestones = await communityAPI.getCommunityMilestones(payload);
      actions.setCommunityMilestones(projectMilestones);
    },
  ),

  addCommunityMilestone: thunk<
    CommunityActionsAndThunks,
    { projectId: number; milestone: Omit<IProjectMilestone, 'id'> }
  >(async (actions, payload) => {
    actions.setCommunityLoading(true);

    const { projectId, milestone } = payload;
    try {
      await communityAPI.createCommunitytMilestone(projectId, milestone);
    } finally {
      actions.setCommunityLoading(false);
    }
  }),

  editCommunityMilestone: thunk<
    CommunityActionsAndThunks,
    { projectId: number; milestone: Omit<IProjectMilestone, 'id'>; milestoneId: number }
  >(async (actions, payload) => {
    actions.setCommunityLoading(true);

    const { projectId, milestone, milestoneId } = payload;
    try {
      await communityAPI.editCommunityMilestone(projectId, milestone, milestoneId);
    } finally {
      actions.setCommunityLoading(false);
    }
  }),

  deleteCommunityMilestone: thunk<CommunityActionsAndThunks, { projectId: number; milestoneId: number }>(
    async (actions, payload) => {
      actions.setCommunityLoading(true);

      const { projectId, milestoneId } = payload;
      try {
        await communityAPI.deleteCommunityMilestone(projectId, milestoneId);
      } finally {
        actions.setCommunityLoading(false);
      }
    },
  ),

  //EVENTS
  loadCommunityEvents: thunk<CommunityActionsAndThunks, { projectId: number; limit: number }>(
    async (actions, payload) => {
      const communitytEvents = await communityAPI.getCommunityEvents(payload);
      actions.setCommunityEvents(communitytEvents);
    },
  ),

  addCommunityEvent: thunk<CommunityActionsAndThunks, { projectId: number; event: Omit<IProjectEvent, 'id'> }>(
    async (actions, payload) => {
      actions.setCommunityLoading(true);

      const { projectId, event } = payload;
      try {
        await communityAPI.createCommunityEvent(projectId, event);
      } finally {
        actions.setCommunityLoading(false);
      }
    },
  ),

  editCommunityEvent: thunk<
    CommunityActionsAndThunks,
    { projectId: number; event: Omit<IProjectEvent, 'id'>; eventId: number }
  >(async (actions, payload) => {
    actions.setCommunityLoading(true);

    const { projectId, event, eventId } = payload;
    try {
      await communityAPI.editCommunityEvent(projectId, event, eventId);
    } finally {
      actions.setCommunityLoading(false);
    }
  }),

  deleteCommunityEvent: thunk<CommunityActionsAndThunks, { projectId: number; eventId: number }>(
    async (actions, payload) => {
      actions.setCommunityLoading(true);

      const { projectId, eventId } = payload;
      try {
        await communityAPI.deleteCommunityEvent(projectId, eventId);
      } finally {
        actions.setCommunityLoading(false);
      }
    },
  ),

  // FAVORITES
  addToFavorites: thunk<CommunityActionsAndThunks, number>(async (actions, payload) => {
    await communityAPI.addToFavorites(payload);
  }),

  removeFromFavorites: thunk<CommunityActionsAndThunks, number>(async (actions, payload) => {
    await communityAPI.removeFromFavorites(payload);
  }),

  loadFavoriteCommunitiesList: thunk<CommunityActionsAndThunks, FilterParams>(async (actions, payload) => {
    actions.setCommunityLoading(true);

    try {
      const favoriteCommunities = await communityAPI.getFavoriteCommunities(payload);
      if (favoriteCommunities.items.length) {
        favoriteCommunities.items.forEach((item) => (item.isFavorite = true));
      }
      actions.setFavoriteCommunitiesList(favoriteCommunities);
    } finally {
      actions.setCommunityLoading(false);
    }
  }),

  burnTokens: thunk<CommunityActionsAndThunks, IBurnTokenPayload, unknown, IStoreModel>(
    async (actions, payload, { getStoreState }) => {
      const wallet = getStoreState().blockchain.wallet;

      const signer = await validateWalletForTransaction({ web3Provider: wallet.provider });

      if (!signer) {
        return;
      }
      const contract = new PlatformContract(signer);
      return contract.burnTokens(payload.tokenAddr, payload.amount);
    },
  ),
};
